import React, { Suspense, useContext, useEffect } from "react";
import { Outlet, useParams, useSearchParams } from "react-router-dom";
import { usePageAccess } from "contexts/CurrentUserContext";
import { TenantContext } from "contexts/TenantContext";
import { SettingsNav } from "components/common/SettingsNav";
import { LoadingState } from "components/common/LoadingState";
import api from "services/api";

// Shared layout for all /settings/* sub-pages. SettingsNav is rendered once
// here and persists across sub-tab navigation via Outlet, instead of each
// page independently mounting its own copy (which caused the whole settings
// screen — nav included — to unmount/remount on every tab switch).
//
// The Outlet has its own local Suspense boundary so a sub-page's lazy chunk
// loading only swaps the inner content, not SettingsNav. Without this, the
// suspend event has no closer boundary to land on and bubbles up to
// SidebarLayout's app-wide Suspense, which unmounts this ENTIRE layout
// (SettingsNav included) and replaces it with the app-wide loading fallback.
const SettingsLayout: React.FC = (): React.ReactElement => {
  const { tenantSlug: slugFromParams } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const { tenantSlug: slugFromUser, tenantId, loading: userLoading } = usePageAccess();
  const tenantSlug = slugFromParams ?? slugFromUser ?? "";
  const campaignKey = searchParams.get("campaign");
  const existingTenantContext = useContext(TenantContext);

  useEffect(() => {
    if (!existingTenantContext && tenantId) {
      api.setTenantId(tenantId);
    }
  }, [existingTenantContext, tenantId]);

  const content = (
    <Suspense fallback={<LoadingState message="Loading..." />}>
      <Outlet />
    </Suspense>
  );

  return (
    <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 p-4 sm:p-6 text-left">
      <SettingsNav tenantSlug={tenantSlug} campaignKey={campaignKey} />
      {existingTenantContext ? (
        content
      ) : (
        <TenantContext.Provider
          value={{
            tenant: tenantSlug ? { id: tenantId || "", slug: tenantSlug, name: tenantSlug, status: "active" } : null,
            tenantId: tenantId || null,
            loading: userLoading,
            error: null,
          }}
        >
          {content}
        </TenantContext.Provider>
      )}
    </div>
  );
};

export default SettingsLayout;
